import * as yup from 'yup';
import { BrowserInfo, CardConfiguration, PaymentMethodData } from '@adyen/adyen-web';

import {
  PaymentMethod,
  EPaymentProvider,
  AdyenAPMFinalizeRequestBody,
  AdyenCardFinalizeRequestBody,
  ShopperName,
} from '../../../../contracts';
import i18n from '../../../../i18n';
import { AdyenCardDetails } from './hooks/use-adyen-card';
import { SavedCardFormProps } from '../SavedCards/SavedCards';
import { AdyenAPMAdditionalInputs } from './types';

export const getCardFormSchema = (shouldHideEmailField?: boolean) => {
  return yup.object().shape({
    email: shouldHideEmailField
      ? yup.string().notRequired()
      : yup
          .string()
          .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
          .email(i18n.t('checkout.fieldErrors.invalidEmail')),
    cardholderName: yup.string().required(i18n.t('checkout.fieldErrors.incompleteCardholderName')),
  });
};

export const apmFormSchema = yup.object().shape({
  email: yup
    .string()
    .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
    .email(i18n.t('checkout.fieldErrors.invalidEmail')),
});

export const ADYEN_CARD_CONFIGURATION: CardConfiguration = {
  // enableStoreDetails: true,
  showContextualElement: false,
  challengeWindowSize: '05',
  exposeExpiryDate: true,
  placeholders: {
    cardNumber: 'Card Number',
    expiryDate: 'MM / YY',
    securityCodeFourDigits: 'CVC',
    securityCodeThreeDigits: 'CVC',
  },
  styles: {
    base: {
      color: '#30313D',
      fontSize: '16px',
      lineHeight: '120px',
      fontWeight: '350',
      fontSmoothing: 'antialiased',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Open Sans, Helvetica Neue, sans-serif',
    },
    error: {
      color: '#DF1B41',
    },
    placeholder: {
      color: '#808080',
      fontWeight: '300',
    },
  },
};

export function createAdyenSavedCardFinalizeRequestBody(
  formValues: SavedCardFormProps,
  selectedPaymentMethod: PaymentMethod
) {
  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: formValues.email,
    paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
    paymentMethodId: selectedPaymentMethod?.id,
    params: {
      paymentToken: formValues.cardID,
      cvc: formValues.securityCode,
      brand: formValues.brand,
      browserInfo: getBrowserInfo(),
    },
  };
}

function getBrowserInfo() {
  return {
    acceptHeader: 'acceptHeader' in navigator ? navigator.acceptHeader : '*/*', // Not directly available, use fallback
    colorDepth: screen.colorDepth || window.screen.colorDepth,
    language: navigator.language,
    javaEnabled: navigator.javaEnabled ? navigator.javaEnabled() : false,
    screenHeight: screen.height || window.screen.height,
    screenWidth: screen.width || window.screen.width,
    userAgent: navigator.userAgent,
    timeZoneOffset: new Date().getTimezoneOffset(),
  };
}

export function createAdyenCardFinalizeRequestBody(
  paymentMethod: PaymentMethodData['paymentMethod'],
  browserInfo: BrowserInfo | undefined,
  selectedPaymentMethod: PaymentMethod | undefined,
  formValues: { cardholderName: string; shouldSaveCard: boolean; email: string },
  cardDetails?: AdyenCardDetails
): AdyenCardFinalizeRequestBody {
  const [expiryMonth, expiryYear] = cardDetails?.expiryDate?.split('/') || [];
  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: formValues.email,
    paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName!,
    paymentMethodId: selectedPaymentMethod?.id!,
    params: {
      browserInfo: browserInfo,
      paymentMethod: paymentMethod,
      cardHolderName: formValues.cardholderName,
      shouldSaveCard: formValues.shouldSaveCard,
      cardDetails: {
        bin: cardDetails?.bin || '',
        lastFourDigits: cardDetails?.endDigits || '',
        nameOnCard: formValues.cardholderName,
        expirationMonth: expiryMonth || '',
        expirationYear: expiryYear || '',
      },
    },
  };
}

function transformNameToShopperName(name?: string): ShopperName | undefined {
  if (!name) {
    return;
  }
  const [firstName, lastName] = name.trim().split(/\s+/);
  return {
    firstName: firstName || '',
    lastName: lastName || '',
  };
}

function parseCanadianAddress(address: string) {
  if (!address || address.trim() === '') {
    throw new Error('Street address is required');
  }

  // Canadian postal code pattern: A1A 1A1
  const postalCodePattern = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/i;
  const postalCodeMatch = address.match(postalCodePattern);
  const postalCode = postalCodeMatch ? postalCodeMatch[0] : '';

  // Remove postal code from address for further parsing
  const addressWithoutPostal = address.replace(postalCodePattern, '').trim();

  // Province pattern (2 uppercase letters)
  const provincePattern = /\b[A-Z]{2}\b/;
  const provinceMatch = addressWithoutPostal.match(provincePattern);
  const province = provinceMatch ? provinceMatch[0] : '';

  // Remove province from remaining string
  const addressWithoutProvince = addressWithoutPostal.replace(provincePattern, '').trim();

  // Split remaining address into parts
  const parts = addressWithoutProvince.split(',').map((part) => part.trim());

  // First part should be street address
  const streetAddress = parts[0];

  // City should be the part before the province
  const city = parts[1] || '';

  // Parse street number and name
  const streetMatch = streetAddress.match(/^(\d+)\s+(.+)/);
  const houseNumberOrName = streetMatch ? streetMatch[1] : '';
  const street = streetMatch ? streetMatch[2] : streetAddress;

  // Validate required fields for Canadian addresses
  if (!street.trim()) {
    throw new Error('Street name is required');
  }

  if (!city.trim()) {
    throw new Error('City is required');
  }

  if (!postalCode.trim()) {
    throw new Error('Postal code is required');
  }

  return {
    street,
    houseNumberOrName,
    city,
    stateOrProvince: province,
    postalCode,
    country: 'CA',
  };
}

export function parseAddress(
  billingAddress?: { address: string; zipCode: string },
  country?: string
) {
  if (!billingAddress) {
    return;
  }

  if (!billingAddress.address || billingAddress.address.trim() === '') {
    throw new Error('Street address is required');
  }

  if (!billingAddress.zipCode || billingAddress.zipCode.trim() === '') {
    throw new Error('Postal code is required');
  }

  if (country === 'CA') {
    return parseCanadianAddress(billingAddress.address);
  }

  const addressParts = billingAddress.address.split(',').map((part) => part.trim());

  if (addressParts.length < 2) {
    throw new Error('Address must include street and city (separated by commas)');
  }

  const streetMatch = addressParts[0].match(/^(\d+)\s+(.+)/);
  const houseNumber = streetMatch?.[1] || '';
  const street = streetMatch?.[2] || '';

  const city = addressParts[1] || '';
  const state = addressParts[2]?.split(' ')[0] || '';

  if (!street.trim()) {
    throw new Error('Street name is required');
  }

  if (!city.trim()) {
    throw new Error('City is required');
  }

  return {
    street,
    houseNumberOrName: houseNumber,
    stateOrProvince: state,
    city,
    country,
    postalCode: billingAddress.zipCode,
  };
}

export function createAdyenAPMFinalizeRequestBody({
  additionalInputValues,
  selectedPaymentMethod,
  country,
}: {
  additionalInputValues: AdyenAPMAdditionalInputs;
  selectedPaymentMethod?: PaymentMethod;
  country?: string;
}): AdyenAPMFinalizeRequestBody {
  const billingAddress = parseAddress(additionalInputValues.billingAddress, country);

  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: additionalInputValues.email || additionalInputValues.shopperEmail,
    paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName!,
    paymentMethodId: selectedPaymentMethod?.id!,
    params: selectedPaymentMethod?.additionalInputFields
      ? {
          additionalInputFields: {
            shopperName: transformNameToShopperName(additionalInputValues.shopperName),
            shopperEmail: additionalInputValues.shopperEmail,
            telephoneNumber: additionalInputValues.telephoneNumber,
            billingAddress,
          },
        }
      : {},
  };
}
